/**
 * Workflow validation — configuration.
 * ════════════════════════════════════
 * Plain, JSON-shaped data for the workflow checks in `workflow-validation.ts`.
 *
 * HOW TO USE
 * ──────────
 * 1) TUNE A CHECK — just edit a value below. e.g. add another callback event to
 *    `callbackEvents`, or change the PAY / EXPIRE pair. No rule code to touch.
 *
 * 2) ADD A NEW CHECK — open `workflow-validation.ts` and append one entry to the
 *    `RULES` registry. A rule looks like this:
 *
 *      {
 *        id: 'my-check',                 // label only (a rule may emit >1 kind)
 *        run: (ctx) => {
 *          const out: Warning[] = [];
 *          // inspect ctx (see below) and push Warning objects
 *          if (somethingWrong) {
 *            out.push({
 *              kind: 'my-check',         // add this literal to the Warning union too
 *              severity: 'warning',      // 'warning' (default) | 'info' (recommendation)
 *              stateCode: 'SOME_STATE',  // optional — makes the warning click-to-jump
 *              message: 'Explain the problem in plain language.',
 *            });
 *          }
 *          return out;
 *        },
 *      }
 *
 *    Then add a test in `workflow-validation.spec.ts`. That's the whole workflow.
 *
 * 3) WHAT `ctx` GIVES YOU (pre-computed from the workflow model):
 *      • cfg         — the values in THIS file
 *      • transitions — every event/transition in the workflow
 *      • allCodes    — every state code referenced anywhere (Set, includes startState)
 *      • inbound     — state codes that some transition lands on (i.e. is an end state)
 *      • endStates   — [{ stateCode, nextEvent, actions }] for every end state
 *
 * SEVERITY
 *      • 'warning' — something is likely broken (amber, always shown as an error)
 *      • 'info'    — a recommendation, not an error (blue)
 *
 * These values mirror the application engine (IntegrationAction / WorkflowServiceImpl):
 *  - "ANY" start state matches any current state.
 *  - an async INTEGRATION calls back via a PUSH event (the app waits); a sync
 *    INTEGRATION never calls back, so the app only advances via a system nextEvent.
 */
export interface IWorkflowValidationConfig {
  /** Entry state — never needs an inbound edge. */
  startState: string;
  /** Wildcard start state — its events apply to every state; never needs an inbound edge. */
  anyState: string;
  /** Events an async integration uses to call back into the workflow. */
  callbackEvents: string[];
  /** actionType prefix that identifies an integration action (INTEGRATION, INTEGRATION_UPDATE…). */
  integrationActionPrefix: string;
  /** Key on an integration action's args that marks it asynchronous. */
  asyncArgKey: string;
  /** Payment events for the "a PAY path needs an EXPIRE_PAYMENT path" rule. */
  payEvent: string;
  expirePaymentEvent: string;
}

export const WORKFLOW_VALIDATION_CONFIG: IWorkflowValidationConfig = {
  startState: 'NEW', // the state every workflow starts from
  anyState: 'ANY', // wildcard: an event from ANY can run in any state
  callbackEvents: ['PUSH', 'PUSH_CALLBACK'], // add more here if new callback events are introduced
  integrationActionPrefix: 'INTEGRATION', // matches INTEGRATION and INTEGRATION_UPDATE
  asyncArgKey: 'async', // action.args.async === true → asynchronous integration
  payEvent: 'PAY',
  expirePaymentEvent: 'EXPIRE_PAYMENT',
};
